import React, { useContext, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import SearchIcon from "@material-ui/icons/Search";
import InputBase from "@material-ui/core/InputBase";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Badge from "@material-ui/core/Badge";
import MoveToInboxIcon from "@material-ui/icons/MoveToInbox";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import GroupIcon from "@material-ui/icons/Group";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import NewTicketModal from "../NewTicketModal";
import TicketsList from "../TicketsListCustom";
import TabPanel from "../TabPanel";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../Can";
import TicketsQueueSelect from "../TicketsQueueSelect";
import { Button } from "@material-ui/core";
import { TagsFilter } from "../TagsFilter";
import { UsersFilter } from "../UsersFilter";
import { ContactsFilter } from "../ContactsFilter";
import useTickets from "../../hooks/useTickets";
import { SocketContext } from "../../context/Socket/SocketContext";
import useSettings from "../../hooks/useSettings";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  ticketsWrapper: {
    position: "relative",
    display: "flex",
    height: "100%",
    flexDirection: "column",
    overflow: "hidden",
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    borderRadius: 0,
  },

  tabsHeader: {
    flex: "none",
    backgroundColor: theme.palette.tabHeaderBackground,
  },

  tabsInternal: {
    flex: "none",
    backgroundColor: theme.palette.tabHeaderBackground,
  },

  settingsIcon: {
    alignSelf: "center",
    marginLeft: "auto",
    padding: 8,
  },

  tab: {
    minWidth: 120,
    width: 120,
  },

  internalTab: {
    minWidth: 120,
    width: 120,
    padding: 5,
  },

  ticketOptionsBox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    background: theme.palette.optionsBackground,
    padding: theme.spacing(1),
  },

  ticketButtons: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },

  ticketSearchLine: {
    padding: theme.spacing(1),
  },

  serachInputWrapper: {
    flex: 1,
    background: theme.palette.total,
    display: "flex",
    borderRadius: 40,
    padding: 4,
    marginRight: theme.spacing(1),
  },

  searchIcon: {
    color: "grey",
    marginLeft: 6,
    marginRight: 6,
    alignSelf: "center",
  },

  searchInput: {
    flex: 1,
    border: "none",
    borderRadius: 30,
  },

  insiderTabPanel: {
    height: "100%",
    marginTop: "-72px",
    paddingTop: "72px",
  },

  insiderDoubleTabPanel: {
    display: "flex",
    flexDirection: "column",
    marginTop: "-72px",
    paddingTop: "72px",
    height: "100%",
  },

  labelContainer: {
    width: "auto",
    padding: 0,
  },
  iconLabelWrapper: {
    flexDirection: "row",
    "& > *:first-child": {
      marginBottom: "3px !important",
      marginRight: 16,
    },
  },
  insiderTabLabel: {
    [theme.breakpoints.down(1600)]: {
      display: "none",
    },
  },
  smallFormControl: {
    "& .MuiOutlinedInput-input": {
      padding: "12px 10px",
    },
    "& .MuiInputLabel-outlined": {
      marginTop: "-6px",
    },
  },
}));

const RECOVERY_NOTIFICATION_TIMEOUT_MS = 3 * 60 * 1000;

const getRecoveryMessageId = (payload = {}) =>
  String(payload.messageId || payload.id || "").trim();

const TicketsManagerTabs = () => {
  const classes = useStyles();
  const history = useHistory();

  const [searchParam, setSearchParam] = useState("");
  const [tab, setTab] = useState("open");
  const [tabOpen, setTabOpen] = useState("open");
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [internalTicketModalOpen, setInternalTicketModalOpen] = useState(false);
  const { user } = useContext(AuthContext);
  const { profile } = user;
  const isAdmin = profile === "admin";
  const [showAllTickets, setShowAllTickets] = useState(isAdmin);
  const searchInputRef = useRef();

  const [openCount, setOpenCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [tabGroup, setTabGroup] = useState("open");
  const [groupOpenCount, setGroupOpenCount] = useState(0);
  const [groupPendingCount, setGroupPendingCount] = useState(0);
  const userQueueIds = user.queues?.map((q) => q.id);
  const [selectedQueueIds, setSelectedQueueIds] = useState(userQueueIds || []);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [ignoreGroupMessages, setIgnoreGroupMessages] = useState(false);

  const socketManager = useContext(SocketContext);
  const { getAll: getAllSettings } = useSettings();
  const { tickets: groupTickets } = useTickets({
    pageNumber: 1,
    withUnreadMessages: true,
    isGroup: true,
    queueIds: JSON.stringify(selectedQueueIds),
  });
  const [groupNotifications, setGroupNotifications] = useState([]);
  const activeRecoveryByMessageIdRef = useRef(new Map());
  const [groupsUnreadCount, setGroupsUnreadCount] = useState(0);
  const shouldShowGroupTab = !ignoreGroupMessages;

  useEffect(() => {
    setSelectedQueueIds(userQueueIds || []);
  }, [user.id, user.queues]);

  useEffect(() => {
    if (tab === "search") {
      searchInputRef.current.focus();
    }
  }, [tab]);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settingList = await getAllSettings();
        const ignoreSetting = settingList.find(
          (setting) => setting.key === "CheckMsgIsGroup",
        );
        setIgnoreGroupMessages(ignoreSetting?.value === "enabled");
      } catch (err) {
        toastError(err);
      }
    };

    fetchSettings();
  }, []);

  useEffect(() => {
    if (ignoreGroupMessages && tab === "group") {
      setTab("open");
    }
  }, [ignoreGroupMessages, tab]);

  let searchTimeout;

  const handleSearch = (e) => {
    const searchedTerm = e.target.value.toLowerCase();

    clearTimeout(searchTimeout);

    if (searchedTerm === "") {
      setSearchParam(searchedTerm);
      setTab("open");
      return;
    }

    searchTimeout = setTimeout(() => {
      setSearchParam(searchedTerm);
    }, 500);
  };

  const handleChangeTab = (e, newValue) => {
    setTab(newValue);
  };

  const handleChangeTabOpen = (e, newValue) => {
    setTabOpen(newValue);
  };

  const applyPanelStyle = (status) => {
    if (tabOpen !== status) {
      return { width: 0, height: 0 };
    }
  };

  const handleChangeTabGroup = (e, newValue) => {
    setTabGroup(newValue);
  };

  const applyGroupPanelStyle = (status) => {
    if (tabGroup !== status) {
      return { width: 0, height: 0 };
    }
  };

  const handleCloseOrOpenTicket = (ticket, setModalOpen) => {
    setModalOpen(false);
    setNewTicketModalOpen(false);
    setInternalTicketModalOpen(false);
    if (ticket !== undefined && ticket.uuid !== undefined) {
      history.push(`/tickets/${ticket.uuid}`);
    }
  };

  const handleSelectedTags = (selecteds) => {
    const tags = selecteds.map((t) => t.id);
    setSelectedTags(tags);
  };

  const handleSelectedUsers = (selecteds) => {
    const users = selecteds.map((t) => t.id);
    setSelectedUsers(users);
  };

  const handleSelectedContact = (contactId) => {
    setSelectedContactId(contactId);
  };

  useEffect(() => {
    const filtered = groupTickets
      .filter((t) => ["open", "pending"].includes(t.status))
      .filter((t) => {
        const groupUsers = t.contact?.groupUsers;
        const hasGroupUsers = Array.isArray(groupUsers);
        if (profile === "user") {
          if (!hasGroupUsers || groupUsers.length === 0) {
            return true;
          }
          return groupUsers.some((gu) => Number(gu.userId) === Number(user.id));
        }
        const members = groupUsers || [];
        return (
          members.length === 0 ||
          members.some((gu) => Number(gu.userId) === Number(user.id))
        );
      });
    setGroupNotifications(filtered);
  }, [groupTickets, profile, user.id]);

  useEffect(() => {
    const count = groupNotifications.reduce(
      (acc, t) => acc + (t.unreadMessagesForUser ?? t.unreadMessages ?? 0),
      0,
    );
    setGroupsUnreadCount(count);
  }, [groupNotifications]);

  useEffect(() => {
    return () => {
      activeRecoveryByMessageIdRef.current.forEach((entry) => {
        if (entry?.timeoutId) {
          clearTimeout(entry.timeoutId);
        }
      });
      activeRecoveryByMessageIdRef.current.clear();
    };
  }, []);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);

    const ticketFilter = (ticket) => {
      if (!ticket || !ticket.isGroup) return false;
      if (!["open", "pending"].includes(ticket.status)) return false;
      if (
        Array.isArray(selectedQueueIds) &&
        selectedQueueIds.length > 0 &&
        ticket.queueId &&
        selectedQueueIds.indexOf(ticket.queueId) === -1
      ) {
        return false;
      }
      const groupUsers = ticket.contact?.groupUsers;
      const hasGroupUsers = Array.isArray(groupUsers);
      if (profile === "user") {
        if (!hasGroupUsers || groupUsers.length === 0) {
          return true;
        }
        return groupUsers.some((gu) => Number(gu.userId) === Number(user.id));
      }
      const members = groupUsers || [];
      return (
        members.length === 0 ||
        members.some((gu) => Number(gu.userId) === Number(user.id))
      );
    };

    socket.on("ready", () => socket.emit("joinNotification"));

    socket.on(`company-${companyId}-ticket`, (data) => {
      setGroupNotifications((prev) => {
        const list = [...prev];
        const ticket = data.ticket;
        const ticketId = data.ticketId || ticket?.id;
        const index = list.findIndex((t) => t.id === ticketId);

        if (data.action === "delete" || data.action === "updateUnread") {
          if (data.userId && Number(data.userId) !== Number(user?.id)) {
            return list;
          }
          if (index !== -1) list.splice(index, 1);
          return list;
        }

        if (data.action === "update") {
          const unreadForUser =
            ticket?.unreadMessagesForUser ?? ticket?.unreadMessages ?? 0;
          if (ticketFilter(ticket) && unreadForUser > 0) {
            if (index !== -1) {
              list[index] = ticket;
            } else {
              list.unshift(ticket);
            }
          } else if (index !== -1) {
            list.splice(index, 1);
          }
          return list;
        }

        return list;
      });
    });

    socket.on(`company-${companyId}-appMessage`, (data) => {
      const payloadMessage = data.message || {};
      const messageId =
        getRecoveryMessageId(payloadMessage) || String(data.messageId || "");

      const clearRecoveryEntry = (id) => {
        if (!id) return;
        const entry = activeRecoveryByMessageIdRef.current.get(id);
        if (entry?.timeoutId) {
          clearTimeout(entry.timeoutId);
        }
        activeRecoveryByMessageIdRef.current.delete(id);
      };

      if (data.action === "incoming_recovery" && messageId) {
        const previous = activeRecoveryByMessageIdRef.current.get(messageId);
        if (previous?.timeoutId) {
          clearTimeout(previous.timeoutId);
        }

        const timeoutId = setTimeout(() => {
          activeRecoveryByMessageIdRef.current.delete(messageId);
        }, RECOVERY_NOTIFICATION_TIMEOUT_MS);

        activeRecoveryByMessageIdRef.current.set(messageId, {
          timeoutId,
          ticketId:
            data.ticket?.id || payloadMessage?.ticketId || data.ticketId,
        });
      }

      if (
        (data.action === "create" ||
          data.action === "incoming_recovery_resolved") &&
        messageId
      ) {
        clearRecoveryEntry(messageId);

        if (data.action === "incoming_recovery_resolved") {
          const resolvedTicketId = Number(
            data.ticket?.id || payloadMessage?.ticketId || data.ticketId || 0,
          );
          if (resolvedTicketId) {
            setGroupNotifications((prev) =>
              prev.filter((ticket) => {
                if (Number(ticket.id) !== resolvedTicketId) return true;
                const unread =
                  ticket.unreadMessagesForUser ?? ticket.unreadMessages ?? 0;
                return unread > 0;
              }),
            );
          }
        }
      }

      if (data.action === "create" && !payloadMessage.fromMe) {
        const ticket = data.ticket;
        if (ticketFilter(ticket)) {
          setGroupNotifications((prev) => {
            const list = [...prev];
            const index = list.findIndex((t) => t.id === ticket.id);
            if (index !== -1) {
              list[index] = ticket;
              return list;
            }
            return [ticket, ...list];
          });
        }
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [socketManager, profile, user.id, selectedQueueIds]);

  return (
    <Paper elevation={0} variant="outlined" className={classes.ticketsWrapper}>
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        onClose={(ticket) => {
          handleCloseOrOpenTicket(ticket, setNewTicketModalOpen);
        }}
      />
      <NewTicketModal
        modalOpen={internalTicketModalOpen}
        isInternal={true}
        contextWhatsappId={null}
        onClose={(ticket) => {
          handleCloseOrOpenTicket(ticket, setInternalTicketModalOpen);
        }}
      />
      <Paper elevation={0} square className={classes.tabsHeader}>
        <Tabs
          value={tab}
          onChange={handleChangeTab}
          variant="fullWidth"
          indicatorColor="primary"
          textColor="primary"
          aria-label="icon label tabs example"
        >
          <Tab
            value={"open"}
            icon={<MoveToInboxIcon />}
            label={i18n.t("tickets.tabs.open.title")}
            classes={{ root: classes.tab }}
          />

          {shouldShowGroupTab && (
            <Tab
              value={"group"}
              icon={
                <Badge
                  className={classes.badge}
                  badgeContent={groupsUnreadCount}
                  color="secondary"
                >
                  <GroupIcon />
                </Badge>
              }
              label={i18n.t("tickets.tabs.group.title")}
              classes={{ root: classes.tab }}
            />
          )}
          <Tab
            value={"closed"}
            icon={<CheckBoxIcon />}
            label={i18n.t("tickets.tabs.closed.title")}
            classes={{ root: classes.tab }}
          />
          <Tab
            value={"search"}
            icon={<SearchIcon />}
            label={i18n.t("tickets.tabs.search.title")}
            classes={{ root: classes.tab }}
          />
        </Tabs>
      </Paper>
      <Paper square elevation={0} className={classes.ticketOptionsBox}>
        {tab === "search" ? (
          <div className={classes.serachInputWrapper}>
            <SearchIcon className={classes.searchIcon} />
            <InputBase
              className={classes.searchInput}
              inputRef={searchInputRef}
              placeholder={i18n.t("tickets.search.placeholder")}
              type="search"
              onChange={handleSearch}
            />
          </div>
        ) : (
          <>
            <div className={classes.ticketButtons}>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setNewTicketModalOpen(true)}
              >
                {i18n.t("ticketsManager.buttons.newTicket")}
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setInternalTicketModalOpen(true)}
              >
                {i18n.t("ticketsManager.buttons.internalTicket")}
              </Button>
            </div>
            <Can
              role={user.profile}
              perform="tickets-manager:showall"
              yes={() => (
                <FormControlLabel
                  label={i18n.t("tickets.buttons.showAll")}
                  labelPlacement="start"
                  control={
                    <Switch
                      size="small"
                      checked={showAllTickets}
                      onChange={() =>
                        setShowAllTickets((prevState) => !prevState)
                      }
                      name="showAllTickets"
                      color="primary"
                    />
                  }
                />
              )}
            />
          </>
        )}
        <TicketsQueueSelect
          style={{ marginLeft: 6 }}
          selectedQueueIds={selectedQueueIds}
          userQueues={user?.queues}
          onChange={(values) => setSelectedQueueIds(values)}
        />
      </Paper>
      <TabPanel value={tab} name="open" className={classes.ticketsWrapper}>
        <Tabs
          value={tabOpen}
          onChange={handleChangeTabOpen}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab
            label={
              <Badge
                className={classes.badge}
                badgeContent={openCount}
                color="primary"
              >
                {i18n.t("ticketsList.assignedHeader")}
              </Badge>
            }
            value={"open"}
          />
          <Tab
            label={
              <Badge
                className={classes.badge}
                badgeContent={pendingCount}
                color="secondary"
              >
                {i18n.t("ticketsList.pendingHeader")}
              </Badge>
            }
            value={"pending"}
          />
        </Tabs>
        <Paper className={classes.ticketsWrapper}>
          <TicketsList
            status="open"
            showAll={showAllTickets}
            selectedQueueIds={selectedQueueIds}
            updateCount={(val) => setOpenCount(val)}
            style={applyPanelStyle("open")}
            isGroup={false}
          />
          <TicketsList
            status="pending"
            selectedQueueIds={selectedQueueIds}
            updateCount={(val) => setPendingCount(val)}
            style={applyPanelStyle("pending")}
            isGroup={false}
          />
        </Paper>
      </TabPanel>
      <TabPanel value={tab} name="closed" className={classes.ticketsWrapper}>
        <TicketsList
          status="closed"
          showAll={true}
          selectedQueueIds={selectedQueueIds}
        />
      </TabPanel>
      {shouldShowGroupTab && (
        <TabPanel value={tab} name="group" className={classes.ticketsWrapper}>
          <Tabs
            value={tabGroup}
            onChange={handleChangeTabGroup}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
          >
            <Tab
              label={
                <Badge
                  className={classes.badge}
                  badgeContent={groupOpenCount}
                  color="primary"
                >
                  {i18n.t("ticketsList.assignedHeader")}
                </Badge>
              }
              value={"open"}
            />
            <Tab
              label={
                <Badge
                  className={classes.badge}
                  badgeContent={groupPendingCount}
                  color="secondary"
                >
                  {i18n.t("ticketsList.pendingHeader")}
                </Badge>
              }
              value={"pending"}
            />
          </Tabs>
          <Paper className={classes.ticketsWrapper}>
            <TicketsList
              status="open"
              isGroup={true}
              selectedQueueIds={selectedQueueIds}
              updateCount={(val) => setGroupOpenCount(val)}
              style={applyGroupPanelStyle("open")}
            />
            <TicketsList
              status="pending"
              isGroup={true}
              showAll={profile === "admin"}
              selectedQueueIds={selectedQueueIds}
              updateCount={(val) => setGroupPendingCount(val)}
              style={applyGroupPanelStyle("pending")}
            />
          </Paper>
        </TabPanel>
      )}
      <TabPanel value={tab} name="search" className={classes.ticketsWrapper}>
        <TagsFilter onFiltered={handleSelectedTags} />
        {isAdmin && <UsersFilter onFiltered={handleSelectedUsers} />}
        <ContactsFilter onFiltered={handleSelectedContact} />
        <TicketsList
          searchParam={searchParam}
          showAll={true}
          tags={selectedTags}
          users={selectedUsers}
          contactId={selectedContactId}
          selectedQueueIds={selectedQueueIds}
        />
      </TabPanel>
    </Paper>
  );
};

export default TicketsManagerTabs;
